import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFieldById } from '/@/hooks/fields';
import { useRecommendationById } from '/@/hooks/my-recommendations/useFilterMyRecommendations';
import { updateActiveRecommendation } from '/@/store/recommendations/actions';
import { getAppInstance } from '/@/store/session/selectors';
import type { Recommendation } from '/@/types/recommendation';

type SavedCropConfig = {
    crop?: string;
    products?: number[];
    plantingDate?: string;
    environment?: number;
    climatePattern?: number;
    soilMoisture?: number;
    [key: string]: unknown;
};

type SavedRecommendation = {
    id: number;
    field?: number;
    userNode?: number;
    modified?: string;
    created?: string;
    commentsStatus?: Recommendation['commentsStatus'];
    crops?: SavedCropConfig[];
    backup?: Record<string, unknown> & { crops?: SavedCropConfig[] };
};

/**
 * Loads a stored recommendation and seeds it into the `activeRecommendation` slice.
 *
 * The v2 sections read their inputs from that slice — it's what lets the result page reuse the
 * wizard's components untouched instead of a read-only fork of each. Seeding is keyed on the
 * recommendation id rather than assuming the wizard left anything behind: it clears the slice
 * on unmount, and it unmounts while this view mounts, so a cold URL load and the hop straight
 * after "Terminar" have to take the same path.
 */
export const useSavedRecommendation = ({
    recommendationId,
    crop = 'corn',
}: {
    recommendationId: number;
    crop?: string;
}) => {
    const dispatch = useDispatch();
    const appInstance = useSelector(getAppInstance);
    const { data: recommendation } = useRecommendationById(recommendationId) as { data?: SavedRecommendation };

    const fieldId = recommendation?.field;
    const { data: field } = useFieldById({ id: fieldId as number }, { enabled: !!fieldId });

    /**
     * `backup` is the snapshot of what the user actually saw, and the detail, PDF and share
     * views all read it back, so it wins over the top-level crops. The two store the selected
     * hybrids under different keys — the top-level record uses `{crop}Products` — so the id
     * list is resolved rather than read from a fixed key.
     */
    const cropConfig = useMemo(() => {
        const saved = recommendation?.backup?.crops?.[0] || recommendation?.crops?.[0];
        if (!saved) return undefined;
        const products = (saved.products || saved[`${crop}Products`] || []) as number[];
        return { ...saved, crop: saved.crop || crop, products };
    }, [recommendation, crop]);

    const seededFor = useRef<number | undefined>(undefined);
    useEffect(() => {
        if (!cropConfig || !fieldId || seededFor.current === recommendationId) return;
        seededFor.current = recommendationId;
        dispatch(
            updateActiveRecommendation({
                appInstance: appInstance.id,
                field: String(fieldId),
                crops: [cropConfig],
            })
        );
    }, [appInstance.id, dispatch, cropConfig, fieldId, recommendationId]);

    useEffect(() => {
        return () => {
            dispatch(updateActiveRecommendation(undefined));
        };
    }, [dispatch]);

    // The PDF and share flows read the stored blob, not the slice — `backup` is the snapshot
    // they render, so it's merged over the record the same way the read-only page does it.
    const parsedRecommendation = useMemo(
        // The stored record is untyped at the API boundary; `Recommendation` is what every
        // consumer of the blob (share, PDF) declares it wants.
        () =>
            recommendation ? ({ ...recommendation, ...recommendation.backup } as unknown as Recommendation) : undefined,
        [recommendation]
    );

    return {
        recommendation,
        parsedRecommendation,
        field,
        cropConfig,
        // The sections can't render before the slice holds this recommendation's config.
        isReady: !!field && seededFor.current === recommendationId,
    };
};
